import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { fetchEventUpdateSocket } from "./systemDuck";
import { Commands } from "../actions/apiActions";
import { WaitingTime } from "../helpers/utils";
import { dateStringDayAndMonthSwapPlaces } from "../helpers/utils";
import { fetchReports } from "./reportsDuck";
import { fetchSettingsAuth } from "./settingsDuck";
import { fetchEdgeDataSocket } from "./devicesDuck";
import { getDevicesListFromDevicesDuck } from "./graphicsDuck";


const calculateNextQAL3Run = (data, lastRun) => {
  let nextRunDateString = "";

  if (lastRun) {
    const weekDays = [
      "Sunday",    // index 0
      "Monday",    // index 1
      "Tuesday",   // index 2
      "Wednesday", // index 3
      "Thursday",  // index 4
      "Friday",    // index 5
      "Saturday",  // index 6
    ];
    const intervalWeeks = data.intervalWeeks;
    const selectedDayIndex = data.day;
    const formattedHour = String(data.startHour).padStart(2, '0');
    const formattedMinute = String(data.startMinute).padStart(2, '0');

    // Parse the lastRun date string into a Date object
    // Example of lastRun = "17.04.2024 10:27:19"
    const dateParts = lastRun.split(" ")[0].split("."); // = [day, month, year]
    const timeParts = lastRun.split(" ")[1].split(":"); // = [hours, minutes, seconds]
    const lastRunDate = new Date(dateParts[2], dateParts[1] - 1, dateParts[0], timeParts[0], timeParts[1], timeParts[2]);

    const currentDayOfWeek = lastRunDate.getDay(); // Day of the week from 0 (Sunday) to 6 (Saturday)

    let daysUntilNextRun = selectedDayIndex - currentDayOfWeek;
    if (daysUntilNextRun < 0) {
      daysUntilNextRun += 7; // If the selected day has already passed this week
    }
    if (daysUntilNextRun === 0 && (lastRunDate.getHours() > data.startHour || (lastRunDate.getHours() === data.startHour && lastRunDate.getMinutes() >= data.startMinute))) {
      daysUntilNextRun += 7; // If today is the selected day but past the run time
    }

    daysUntilNextRun += 7 * intervalWeeks; // Add the weeks interval

    let nextRunDate = lastRunDate;
    nextRunDate.setDate(lastRunDate.getDate() + daysUntilNextRun); // Set the correct future date
    nextRunDate.setHours(data.startHour, data.startMinute, 0, 0); // Set time precisely to the start time

    const formattedMonth = (nextRunDate.getMonth() + 1).toString().padStart(2, '0');
    const formattedDay = nextRunDate.getDate().toString().padStart(2, '0');

    nextRunDateString = `${weekDays[selectedDayIndex]} ${formattedDay}.${formattedMonth} ${formattedHour}:${formattedMinute}`;
  }

  return nextRunDateString;
};

// TODO: Check if needed and remove if not. Incase mandatory, generalize.
const generateCommandsStatusList = (devices, state) => {
  let newCommandsStatusList = [];

  devices.forEach(device => {
    if (device.hwId === "ATEX_IO") {
      device.diagnostics.forEach(diag => {
        if (diag.displayName === "InstrumentAirValve" || diag.displayName === "CalibrationValve") {
          const isCommandOpen = diag.value.includes("Open") ? true : false;
          const commandName = diag.displayName === "InstrumentAirValve" ? "Air" : "Gas";
          const commandsStatusForSingleDevice = {
            hwId: device.hwId,
            commandName: commandName,
            isCommandOpen: isCommandOpen,
          };
          newCommandsStatusList.push(commandsStatusForSingleDevice);
        }
      });
    }
    if (device.hwId === "SO2_SCRUBBER") {
      device.diagnostics.forEach(diag => {
        if (diag.displayName === "Position") {
          const diagValue = diag.value;
          const commandsStatusForSingleDevice = {
            hwId: device.hwId,
            commandName: diagValue, // Right/Left/Bypass or "-" when data is not yet received
            isCommandOpen: true, // for scrubber, the valve (or position) is always open. commandName is either Right/Left/Bypass and it's open.
          };
          newCommandsStatusList.push(commandsStatusForSingleDevice);
        }
      });
    }
    if (device.hwId === "DIGI_SCU") {
      device.diagnostics.forEach(diag => {
        if (diag.displayName === "Air" || diag.displayName === "Span1" || diag.displayName === "Span2") {
          const isCommandOpen = diag.value === "Open" ? true : false;
          const commandsStatusForSingleDevice = {
            hwId: device.hwId,
            commandName: diag.displayName,
            isCommandOpen: isCommandOpen,
          };
          newCommandsStatusList.push(commandsStatusForSingleDevice);
        }
        if (diag.displayName === "Cal. Path") {
          const isCommandOpen = diag.value === "Probe" ? true : false; // When cal path is "probe" in UI that means the button is selected
          const commandsStatusForSingleDevice = {
            hwId: device.hwId,
            commandName: "System/Probe",
            isCommandOpen: isCommandOpen,
          };
          newCommandsStatusList.push(commandsStatusForSingleDevice);
        }

      });
    }
  });
  let existingCommandsStatusList = state.overview.commandsStatusList;

  newCommandsStatusList.forEach(newItem => {
    let filteredExistingList;
    if (newItem.hwId === "SO2_SCRUBBER") {
      // For SO2_SCRUBBER, filter out any existing items by hwId
      filteredExistingList = existingCommandsStatusList.filter(existingItem => existingItem.hwId !== "SO2_SCRUBBER");
    } else {
      // For all other items, filter out by commandName 
      filteredExistingList = existingCommandsStatusList.filter(existingItem => existingItem.commandName !== newItem.commandName);
    }
    // Update the existingCommandsStatusList
    existingCommandsStatusList = [...filteredExistingList, newItem];
  });

  return existingCommandsStatusList;
};

export const uiDefaultState = {
  overview: {
    commandsStatusList: [], // here is the data on devices actice and inactive commands (isCommandOpen: true/false)
    diagnosticsPickerList: [],
    lastUserCommandFromOverview: {  // this is the last given command by user, we use it to compare the current state of commandsStatusList and the user's last command
      hwId: "",
      command: "",
      isActivating: false,
      isPending: false, // this is for scrubber
    },
    currentPendingCommands: []
  },
  serviceMode: {
    isActive: false,
    isExpanded: false,
    isAuto: true,
    status: "Auto",
    isServiceModeBtnDisabled: false,
    isAutoOrManualBtnDisabled: false,
  },
  settings: {
    language: "en",
  },
  alarms: {},
  alarmsHistory: {
    selectedTab: "all",
  },
  edgeUnitStatusInfo: {
    runActive: false,
    autoZero: {
      status: "",
      generalAlarm: "",
      lastRun: "",
      nextRun: "",
    },
    QAL3: {
      status: "",
      generalAlarm: "",
      lastRun: "",
      nextRun: "",
    },
    CPU: {
      // dynamic keys based on the data received from backend, for example: AvailableDiskSpaceGB, FreeMemoryGB, etc.
    },
  },
};

export const sendCommand = createAsyncThunk("ui/sendCommand", async (data, thunk) => {
  const { systemId, argsProps, commandType, group } = data; // TODO: Check if commandType is needed and remove if not
  const { config } = thunk.getState().config[systemId].system;
  let response = "NoToggle";
  if (config) {
    const args = {
      ...argsProps,
      origin: config.APP_ENV,
      customerId: config.CUSTOMER_ID,
      deviceId: config.DEVICE_ID,
    };
    const { latestAlert } = thunk.getState().config[systemId].system;

    if (commandType && commandType.isToggle === false) {
      response = await Commands.post(config.COMMAND_URL, args, {
        timeout: WaitingTime,
      });
      return {
        systemId,
        data: {
          hwId: args.HwId,
          commandName: args.Value,
          isCommandOpen: false,
        },
        group,
        commandType,
        latestAlert,
        args,
      };
    } else {
      response = await Commands.post(config.COMMAND_URL, args, {
        timeout: WaitingTime,
      });
      return {
        systemId,
        args,
        group,
        commandType,
        latestAlert,
      };
    }
  }
});

// Function to format byte values 
const formatDeviceInfo = (datapoint) => {
  const result = { ...datapoint };
  // Convert bytes to GB
  if (result.Unit?.toLowerCase() === "bytes") {
    result.Value = result.Value / (1024 * 1024 * 1024);
    result.Unit = "GB";
  }
  if (result.ValueName?.startsWith("AvailableDiskSpaceBytes")) {
    result.ValueName = result.ValueName.replace("AvailableDiskSpaceBytes", "Free ");
  }
  if (result.ValueName?.startsWith("TotalDiskSpaceBytes")) {
    result.ValueName = result.ValueName.replace("TotalDiskSpaceBytes", "Size ");
  }
  if (result.ValueName === "HostUptimeSeconds") {
    result.Value = result.Value / 86400.0;
    result.Unit = "d";
  }
  return result;
};

const uiSlice = createSlice({
  name: "ui",
  initialState: uiDefaultState,
  reducers: {
    // setDiagnosticsPickerList(state, action) {
    //   const { valueName, hwId } = action.payload.data;
    //   const filtered = state.overview.diagnosticsPickerList.filter(
    //     (diag) => diag.valueName === valueName && diag.hwId === hwId
    //   );
    //   if (filtered.length > 0) {
    //     _.remove(
    //       state.overview.diagnosticsPickerList,
    //       (diagnosticsItem) =>
    //         diagnosticsItem.valueName === valueName && diagnosticsItem.hwId === hwId
    //     );
    //   } else {
    //     state.overview.diagnosticsPickerList.push(action.payload.data);
    //   }
    // },
    setDiagnosticsPickerList(state, action) {
      const { valueName, hwId } = action.payload.data;

      // Ensure overview object exists
      if (!state.overview) {
        state.overview = {};
      }

      // Ensure diagnosticsPickerList is an array
      if (!Array.isArray(state.overview.diagnosticsPickerList)) {
        state.overview.diagnosticsPickerList = [];
      }

      const existingIndex = state.overview.diagnosticsPickerList.findIndex(
        (diag) => diag.valueName === valueName && diag.hwId === hwId
      );

      if (existingIndex !== -1) {
        // Remove the existing entry
        state.overview.diagnosticsPickerList = state.overview.diagnosticsPickerList.filter(
          (diag) => !(diag.valueName === valueName && diag.hwId === hwId)
        );
      } else {
        // Add new item
        state.overview.diagnosticsPickerList = [
          ...state.overview.diagnosticsPickerList,
          action.payload.data,
        ];
      }
    },

    setServiceModeExpanded(state, action) {
      state.serviceMode.isExpanded = action.payload.data;
    },
    setCommandButtonDisabled(state, action) {
      const { commandType } = action.payload.data;
      if (commandType === "serviceMode") {
        state.serviceMode.isServiceModeBtnDisabled = action.payload.data.isServiceModeBtnDisabled;
      } else if (commandType === "AutoOrManual") {
        state.serviceMode.isAutoOrManualBtnDisabled = action.payload.data.isAutoOrManualBtnDisabled;
      }
    },
    setAlarmHistorySelectedTab(state, action) {
      state.alarmsHistory.selectedTab = action.payload.data.selectedTab;
    },
    setLastUsedCommandFromOverview(state, action) {
      state.overview.lastUserCommandFromOverview.command = action.payload.command;
      state.overview.lastUserCommandFromOverview.isActivating = action.payload.isActivating;
      state.overview.lastUserCommandFromOverview.hwId = action.payload.hwId;
      state.overview.lastUserCommandFromOverview.isPending = action.payload.isPending ? action.payload.isPending : false;
      // state.overview.currentPendingCommands.push(action.payload);
    },
    removeCurrentPendingCommandFromOverview(state, action) {
      state.overview.currentPendingCommands = state.overview.currentPendingCommands.filter(item => item.hwId !== action.payload.hwId
        && item.command !== action.payload.command
        && item.systemId !== action.payload.systemId);
    },
    // Clears a stale pending command indicator (spinner) from the Overview's CommandsList.
    // This is used as a fallback timeout for when a device is offline/unreachable and never
    // confirms the command through the websocket, which previously left the spinner stuck forever.
    // Only clears if it still refers to the same command, so it doesn't wipe out a newer pending command.
    clearPendingCommandFromOverview(state, action) {
      const { hwId, command } = action.payload;
      if (
        state.overview.lastUserCommandFromOverview.hwId === hwId &&
        state.overview.lastUserCommandFromOverview.command === command
      ) {
        state.overview.lastUserCommandFromOverview = {
          hwId: "",
          command: "",
          isActivating: false,
          isPending: false,
        };
      }
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchEventUpdateSocket, (state, action) => {
        const {
          HwId: hwId,
          Element: element,
          Status: status,
          Reason: reason,
          Category: category,
        } = action.payload.data;
        if (element === "ServiceMode") {
          if (status === "On") {
            state.serviceMode.isActive = true;
            state.serviceMode.isExpanded = true;
          } else if (status === "Off") {
            state.serviceMode.isActive = false;
            state.serviceMode.isExpanded = false;
          }
        } else if (element === "ManualMode") {
          let statusStr = "";
          if (status === "On") {
            statusStr = "ManualActive";
          } else if (status === "Off") {
            statusStr = "AutoActive";
          }
          state.serviceMode.status = statusStr;
        } else if (element === "AutoZero") {
          // STATUS PANEL AUTOZERO MESSAGE
          state.edgeUnitStatusInfo.autoZero.status = reason;
          // SERVICEMODE BUTTON STATE HANDLING ACCORDING TO ROUTINE-RUN STATUS MESSAGES
          if (reason.includes("started")) {
            state.serviceMode.isServiceModeBtnDisabled = true;
            state.serviceMode.isAutoOrManualBtnDisabled = true;
            state.edgeUnitStatusInfo.runActive = true;
          } else if (
            reason.includes("resumed") ||
            reason.includes("resumed from failure") ||
            reason.includes("finished")
          ) {
            state.serviceMode.isServiceModeBtnDisabled = false;
            state.serviceMode.isAutoOrManualBtnDisabled = false;
            state.edgeUnitStatusInfo.runActive = false;
          }
        } else if (element === "QAL3") {
          // STATUS PANEL QAL3 MESSAGE
          state.edgeUnitStatusInfo.QAL3.status = reason;
          // SERVICEMODE BUTTON STATE HANDLING ACCORDING TO ROUTINE-RUN STATUS MESSAGES
          if (reason.includes("started")) {
            state.serviceMode.isServiceModeBtnDisabled = true;
            state.serviceMode.isAutoOrManualBtnDisabled = true;
            state.edgeUnitStatusInfo.runActive = true;
          } else if (
            reason.includes("resumed") ||
            reason.includes("resumed from failure") ||
            reason.includes("finished")
          ) {
            state.serviceMode.isServiceModeBtnDisabled = false;
            state.serviceMode.isAutoOrManualBtnDisabled = false;
            state.edgeUnitStatusInfo.runActive = false;
          }
        }
      })
      .addCase(fetchEdgeDataSocket.fulfilled, (state, action) => {
        action.payload.data.forEach((dataPoint) => {
          if (dataPoint?.HwId === "EdgeDevice") {
            if ((dataPoint.ValueName === "AutoZero") || (dataPoint.ValueName === "QAL3")) { return; } // dont push autozero and qal3 statuses to CPU info panel
            // push datapointObject to CPU state. Set ValueName to be key and rest of the object to be value
            dataPoint = formatDeviceInfo(dataPoint);
            state.edgeUnitStatusInfo.CPU[dataPoint.ValueName] = dataPoint;

          } else if (dataPoint?.HwId === "SO2_SCRUBBER") {
            // When we receive dataPoint about scrubber valve state, assume that the last command user has sent is not pending anymore
            if (dataPoint?.ValueName === "ScrubberState" && state.overview.lastUserCommandFromOverview?.isPending) {
              state.overview.lastUserCommandFromOverview.isPending = false;
            }
          }
        });
      })
      // Update commandsStatusList with data from devices
      .addCase(getDevicesListFromDevicesDuck.fulfilled, (state, action) => {
        const devices = action.payload.devicesList;
        const newState = generateCommandsStatusList(devices, state);
        state.overview.commandsStatusList = newState; // Update the state
      })
      .addCase(fetchReports.fulfilled, (state, action) => {
        if (action.payload.name === "AutoZero") {
          //STATUS PANEL AUTOZERO MESSAGES
          if (action.payload.response.length > 0) {
            state.edgeUnitStatusInfo.autoZero.status = action.payload.response[0].State;
            state.edgeUnitStatusInfo.autoZero.lastRun = dateStringDayAndMonthSwapPlaces(
              action.payload.response[0].ReportTimeStamp
            );
          }
        } else if (action.payload.name === "QAL3") {
          //STATUS PANEL QAL3 MESSAGES
          if (action.payload.response.length > 0) {
            state.edgeUnitStatusInfo.QAL3.status = action.payload.response[0].State;
            state.edgeUnitStatusInfo.QAL3.lastRun = dateStringDayAndMonthSwapPlaces(
              action.payload.response[0].ReportTimeStamp
            );
          }
        }
      })
      .addCase(fetchReports.rejected, (state, action) => {
        console.log("fetch reports rejected");
      })
      .addCase(fetchSettingsAuth.fulfilled, (state, action) => {
        //STATUS PANEL AUTOZERO MESSAGES
        if (
          action.payload.settingName === "AutoZeroSettings" &&
          action.payload.response.jsonData &&
          action.payload.response.jsonData.startHour
        ) {
          let formattedHour = String(action.payload.response.jsonData.startHour).padStart(2, '0');
          let formattedMinute = String(action.payload.response.jsonData.startMinute).padStart(2, '0');
          state.edgeUnitStatusInfo.autoZero.nextRun = formattedHour + ":" + formattedMinute;
        }
        //STATUS PANEL QAL3 MESSAGES
        if (
          action.payload.settingName === "QAL3Settings" &&
          action.payload.response.jsonData &&
          action.payload.response.jsonData.startHour
        ) {
          const lastRun = state.edgeUnitStatusInfo.QAL3.lastRun;
          const nextRunDate = calculateNextQAL3Run(action.payload.response.jsonData, lastRun);
          state.edgeUnitStatusInfo.QAL3.nextRun = nextRunDate;
        }
      })
      .addCase(sendCommand.pending, (state, action) => { })
      .addCase(sendCommand.fulfilled, (state, action) => {
        const { latestAlert, args } = action.payload;
        if (
          latestAlert &&
          latestAlert.command === args.Command &&
          latestAlert.value === args.Value
        ) {
          return;
        } else {
          // If group is Scrubber or valves, do not set the isCommandOpen to true, we wait for that info to come trough websocket
          if (action.payload.group === "Scrubber" || action.payload.group === "Valves") {
            // NO NEED TO ALTER STATE, STATE UPDATES TROUGH WEBSOCKET;
            return;
          }
          // if not toggle command
          else if (action.payload.commandType && action.payload.commandType.isToggle === false) {
            // NOT A TOGGLE COMMAND. NO NEED TO ALTER STATE;
            return;
          } else {
            const { commandType } = action.payload;
            if (commandType === "serviceMode") {
              const currentActive = state.serviceMode.isActive;
              state.serviceMode.isActive = !currentActive;
              state.serviceMode.isServiceModeBtnDisabled = false;
              state.serviceMode.isExpanded = !currentActive;
            } else if (commandType === "Auto" || commandType === "Manual") {
              state.serviceMode.isAutoOrManualBtnDisabled = false;
              state.overview.lastUserCommandFromOverview = {
                hwId: "",
                command: "",
                isActivating: false,
                isPending: false,
              };
            }
            else {
              /* 
              HERE WE HANDLE TOGGLE COMMANDS THAT DO NOT RECEIVE STATUS INFO THROUGH WEBSOCKET 
              */
              const { HwId, Value } = action.payload.args;
              const isCommandOpen = action.payload.commandType.isActivating;

              // Filter out the existing item if it exists
              const filteredCommandsStatusList = state.overview.commandsStatusList.filter(item => item.commandName !== Value);
              // Create the updated/new command item
              const updatedCommandItem = {
                hwId: HwId,
                commandName: Value,
                isCommandOpen: isCommandOpen,
              };

              // Concatenate the filtered array with the updated/new item
              state.overview.commandsStatusList = [...filteredCommandsStatusList, updatedCommandItem];

            }
          }
        }
      })
      .addCase(sendCommand.rejected, (state, action) => {
        const commandType = action.payload?.commandType ?? action.meta?.arg?.commandType;
        if (commandType === "serviceMode") {
          state.serviceMode.isServiceModeBtnDisabled = false;
        } else if (commandType === "Auto" || commandType === "Manual") {
          state.serviceMode.isAutoOrManualBtnDisabled = false;
        } else if (commandType === "AutoZero") {
        } else {
          // Regular Overview command buttons (CommandsList) pass an object commandType
          // ({ isToggle, isActivating }). If the request fails outright (e.g. device/network
          // unreachable), clear the pending indicator immediately instead of leaving the
          // button's spinner stuck until the (long) request timeout elapses.
          state.overview.lastUserCommandFromOverview = {
            hwId: "",
            command: "",
            isActivating: false,
            isPending: false,
          };
        }
      });
  },
});

export const {
  setDiagnosticsPickerList,
  setServiceModeExpanded,
  setCommandButtonDisabled,
  setAlarmHistorySelectedTab,
  setLastUsedCommandFromOverview,
  removeCurrentPendingCommandFromOverview,
  clearPendingCommandFromOverview
} = uiSlice.actions;

export default uiSlice.reducer;
